import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import UserRowSkeleton from '../components/Skeleton/UserRowSkeleton';
import Swal from 'sweetalert2';
import {
  Users as UsersIcon,
  UserCheck,
  RotateCcw,
  AlertTriangle,
  Search,
  Filter,
  ArrowUpDown,
  Check,
  ChevronDown,
  Eye,
  Edit2,
  ShieldBan,
  ShieldCheck,
  UserCog,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { formatCurrency } from '../helpers';

interface User {
  id: number | string;
  phone?: string;
  name: string;
  email?: string;
  password2?: string;
  withdrawal_password?: string;
  referalcode?: string;
  refree?: string;
  actual_total_deposit_amount?: number;
  actual_total_withdrawal_amount?: number;
  available_inr?: number;
  is_blocked?: boolean;
  is_demo?: boolean;
  address?: string;
  created_at?: string;
  created?: string;
  assigned_agent_id?: number | string;
  agent_name?: string;
  agent_code?: string;
  agent_uid?: number | string;
  backoffice_name?: string;
  backoffice_id?: number | string;
  registration_source?: string;
}

const Users: React.FC = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'all' | 'valid' | 'fake'>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string | number>>(new Set());

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeActionMenu, setActiveActionMenu] = useState<string | number | null>(null);

  const [editUserData, setEditUserData] = useState({
    uid: '',
    username: '',
    password: '',
    withdrawal_password: '',
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch users when debouncedSearch or page changes
  useEffect(() => {
    fetchUsersData();
  }, [debouncedSearch, page]);

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/getUserData', {
        params: { page, limit, search: debouncedSearch },
      });
      setUsers(res.data.users || []);
      setTotalUsers(res.data.totalUsers || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user: User) => {
    setEditUserData({
      uid: String(user.id),
      username: user.name,
      password: '',
      withdrawal_password: user.withdrawal_password || '',
    });
    setIsModalOpen(true);
    setActiveActionMenu(null);
  };

  const handleEditUser = async () => {
    try {
      await api.put('/editProfile', {
        uid: editUserData.uid,
        username: editUserData.username,
        password: editUserData.password || undefined,
        withdrawal_password: editUserData.withdrawal_password || undefined,
      });
      Swal.fire({
        icon: 'success',
        title: 'User Updated',
        text: 'User details have been successfully updated.',
      });
      fetchUsersData();
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: 'Failed to update user details. Please try again.',
      });
    }
  };

  const handleBlockUnBlockUser = async (uid: string | number, isBlocked?: boolean) => {
    setActiveActionMenu(null);
    let block_reason = null;

    if (!isBlocked) {
      const result = await Swal.fire({
        title: 'Block User',
        input: 'textarea',
        inputLabel: 'Reason for blocking',
        inputPlaceholder: 'Enter block reason...',
        inputAttributes: { maxlength: '255' },
        showCancelButton: true,
        confirmButtonText: 'Block',
        confirmButtonColor: '#ef4444',
        cancelButtonText: 'Cancel',
        inputValidator: (value) => {
          if (!value) return 'Block reason is required';
        },
      });
      if (!result.isConfirmed) return;
      block_reason = result.value;
    }

    try {
      await api.put('/blockUser', {
        uid,
        is_blocked: !isBlocked,
        block_reason,
      });

      fetchUsersData();
      Swal.fire({
        icon: 'success',
        title: `User ${isBlocked ? 'unblocked' : 'blocked'}`,
        text: `User has been successfully ${isBlocked ? 'unblocked' : 'blocked'}.`,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleMakeDemo = async (uid: string | number, isDemo?: boolean) => {
    setActiveActionMenu(null);
    try {
      const result = await Swal.fire({
        title: isDemo ? 'Remove Demo User' : 'Make Demo User',
        text: isDemo
          ? 'Are you sure you want to remove this user from demo mode?'
          : 'Are you sure you want to make this user a demo collaborator?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        confirmButtonColor: '#3b82f6',
      });

      if (!result.isConfirmed) return;

      await api.put('/makeDemoUser', {
        uid,
        is_demo: !isDemo,
      });

      Swal.fire({
        icon: 'success',
        title: `User ${isDemo ? 'removed from demo' : 'made demo user'}`,
        text: `User has been successfully ${isDemo ? 'removed from demo' : 'made demo user'}.`,
      });

      fetchUsersData();
    } catch (err) {
      console.error(err);
    }
  };

  // Filter users based on active tab
  const filteredUsers = users.filter((user) => {
    if (activeTab === 'valid') return !user.is_blocked;
    if (activeTab === 'fake') return user.is_blocked || user.is_demo;
    return true;
  });

  // Calculate quick metrics for the top cards
  const activeCount = users.filter((u) => !u.is_blocked).length;
  const fakeCount = users.filter((u) => u.is_blocked).length;
  const returnRatePercent = totalUsers > 0 ? Math.round((activeCount / totalUsers) * 100) : 0;

  // Toggle selection
  const toggleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map((u) => u.id)));
    }
  };

  const toggleSelectUser = (id: string | number) => {
    const next = new Set(selectedUserIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedUserIds(next);
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-6 pb-10">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Users</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage user accounts, roles and verification status</p>
        </div>

      </div>

      {snackbar && (
        <div className="rounded-lg bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm flex items-center justify-between">
          <span>{snackbar}</span>
          <button onClick={() => setSnackbar(null)} className="text-green-600 hover:text-green-800">
            <X size={16} />
          </button>
        </div>
      )}

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Users */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
              <UsersIcon size={20} />
            </div>
          </div>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {totalUsers.toLocaleString()}
              </h3>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
              Accounts
            </span>
          </div>
        </div>

        {/* Card 2: Active Users */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600">
              <UserCheck size={20} />
            </div>
          </div>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Active users</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {Math.max(0, totalUsers - fakeCount).toLocaleString()}
              </h3>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">
              Active
            </span>
          </div>
        </div>

        {/* Card 3: Return user rate */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center text-sky-600">
              <RotateCcw size={20} />
            </div>
          </div>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Return user rate</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {returnRatePercent}%
              </h3>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
              Rate
            </span>
          </div>
        </div>

        {/* Card 4: Fake accounts */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-600">
              <AlertTriangle size={20} />
            </div>
          </div>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Fake accounts</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {fakeCount}
              </h3>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700">
              Flagged
            </span>
          </div>
        </div>
      </div>

      {/* Tabs Row */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${activeTab === 'all'
            ? 'border-blue-600 text-blue-600 bg-white'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
        >
          All
        </button>
        <button
          onClick={() => setActiveTab('valid')}
          className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${activeTab === 'valid'
            ? 'border-blue-600 text-blue-600 bg-white'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
        >
          Valid accounts
        </button>
        <button
          onClick={() => setActiveTab('fake')}
          className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${activeTab === 'fake'
            ? 'border-blue-600 text-blue-600 bg-white'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
        >
          Fake accounts
        </button>
      </div>

      {/* Table Container with Preline Header Toolbar */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              <Search size={16} />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email or UID"
              className="block w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50/50 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                Swal.fire({
                  title: 'Import / Export',
                  text: 'Export current users list to CSV or Excel.',
                  icon: 'info',
                  confirmButtonColor: '#2563eb',
                });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors"
            >
              <ArrowUpDown size={15} className="text-gray-500" />
              Import / Export
              <ChevronDown size={14} className="text-gray-400" />
            </button>

            <button
              onClick={() => {
                setActiveTab(activeTab === 'all' ? 'valid' : activeTab === 'valid' ? 'fake' : 'all');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors"
            >
              <Filter size={15} className="text-gray-500" />
              Filter
              <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-gray-900 text-white">
                {activeTab === 'all' ? '5' : '1'}
              </span>
            </button>
          </div>
        </div>

        {/* Responsive Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="py-3 px-4 w-10">
                  <input
                    type="checkbox"
                    checked={filteredUsers.length > 0 && selectedUserIds.size === filteredUsers.length}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1 cursor-pointer select-none">
                    Name
                    <ArrowUpDown size={12} className="text-gray-400" />
                  </div>
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1 cursor-pointer select-none">
                    Agent / Store
                    <ArrowUpDown size={12} className="text-gray-400" />
                  </div>
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1 cursor-pointer select-none">
                    Back Office
                    <ArrowUpDown size={12} className="text-gray-400" />
                  </div>
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1 cursor-pointer select-none">
                    Contact & Ref
                    <ArrowUpDown size={12} className="text-gray-400" />
                  </div>
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1 cursor-pointer select-none">
                    Signed up as
                    <ArrowUpDown size={12} className="text-gray-400" />
                  </div>
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1 cursor-pointer select-none">
                    Email
                    <ArrowUpDown size={12} className="text-gray-400" />
                  </div>
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1 cursor-pointer select-none">
                    Financials
                    <ArrowUpDown size={12} className="text-gray-400" />
                  </div>
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <UserRowSkeleton columns={9} rows={8} />
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400">
                    No users found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, i) => {
                  const deposit = user.actual_total_deposit_amount || 0;
                  const withdrawal = user.actual_total_withdrawal_amount || 0;
                  const balance = user.available_inr || 0;
                  const isSelected = selectedUserIds.has(user.id);

                  return (
                    <tr
                      key={user.id}
                      className={`hover:bg-gray-50/70 transition-colors ${isSelected ? 'bg-blue-50/40' : ''
                        }`}
                    >
                      {/* Checkbox */}
                      <td className="py-3.5 px-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectUser(user.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>

                      {/* Name & Avatar */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-xs shadow-sm flex-shrink-0">
                            {getInitials(user.name)}
                          </div>
                          <div>
                            <button
                              onClick={() => navigate(`/users/profile/${user.id}`)}
                              className="font-medium text-gray-900 hover:text-blue-600 transition-colors text-sm text-left block"
                            >
                              {user.name}
                            </button>
                            <span className="text-xs text-gray-400 font-mono">UID: {user.id}</span>
                          </div>
                        </div>
                      </td>

                      {/* Agent / Store */}
                      <td className="py-3.5 px-4">
                        {user.agent_name ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 font-medium text-gray-900 text-sm">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block flex-shrink-0"></span>
                              <span className="truncate max-w-[130px]" title={user.agent_name}>{user.agent_name}</span>
                            </div>
                            <div className="text-xs text-gray-400 font-mono mt-0.5">
                              {user.agent_code || (user.agent_uid ? `UID: #${user.agent_uid}` : '')}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 font-medium italic">Direct (No Agent)</span>
                        )}
                      </td>

                      {/* Back Office */}
                      <td className="py-3.5 px-4">
                        {user.backoffice_name ? (
                          <div className="flex flex-col items-start">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              {user.backoffice_name}
                            </span>
                            {user.backoffice_id && (
                              <span className="text-[11px] text-gray-400 font-mono mt-0.5 ml-1">
                                UID: #{user.backoffice_id}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 font-medium italic">Direct / Super Admin</span>
                        )}
                      </td>

                      {/* Contact & Reference */}
                      <td className="py-3.5 px-4 text-gray-600">
                        <div className="text-sm font-medium text-gray-800">{user.phone || 'N/A'}</div>
                        <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                          <span>Ref: {user.referalcode || 'None'}</span>
                          {user.registration_source && (
                            <span className="px-1.5 py-0.2 bg-gray-100 text-gray-600 rounded text-[10px] uppercase font-semibold">
                              {user.registration_source}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Signed up as Badge */}
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col gap-1 items-start">
                          {user.is_blocked ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                              <AlertTriangle size={11} className="text-amber-600" />
                              Fake account
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                              <Check size={11} className="text-emerald-600" />
                              Valid account
                            </span>
                          )}

                          {user.is_demo && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                              Demo User
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Email */}
                      <td className="py-3.5 px-4 text-gray-600 font-normal">
                        {user.email || 'N/A'}
                      </td>

                      {/* Financials: Deposit, Withdrawal, Balance */}
                      <td className="py-3.5 px-4">
                        <div className="text-xs space-y-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-400">Bal:</span>
                            <span className={`font-semibold ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(balance)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2 text-[11px] text-gray-400">
                            <span>Dep: {formatCurrency(deposit)}</span>
                            <span>With: {formatCurrency(withdrawal)}</span>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => navigate(`/users/profile/${user.id}`)}
                            title="View Profile"
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            onClick={() => handleOpenModal(user)}
                            title="Edit User"
                            className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            onClick={() => handleBlockUnBlockUser(user.id, user.is_blocked)}
                            title={user.is_blocked ? 'Unblock User' : 'Block User'}
                            className={`p-1.5 rounded-lg transition-colors ${user.is_blocked
                              ? 'text-amber-600 hover:bg-amber-50'
                              : 'text-red-500 hover:bg-red-50'
                              }`}
                          >
                            {user.is_blocked ? <ShieldCheck size={15} /> : <ShieldBan size={15} />}
                          </button>
                          <button
                            onClick={() => handleMakeDemo(user.id, user.is_demo)}
                            title={user.is_demo ? 'Remove Demo' : 'Make Demo'}
                            className={`p-1.5 rounded-lg transition-colors ${user.is_demo
                              ? 'text-purple-600 hover:bg-purple-50'
                              : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                              }`}
                          >
                            <UserCog size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Clean Preline-Style Pagination Footer */}
        <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500 bg-white">
          <div>
            Showing <span className="font-medium text-gray-900">{(page - 1) * limit + 1}</span> to{' '}
            <span className="font-medium text-gray-900">
              {Math.min(page * limit, totalUsers || filteredUsers.length)}
            </span>{' '}
            of <span className="font-medium text-gray-900">{totalUsers || filteredUsers.length}</span> results
          </div>

          <div className="inline-flex items-center gap-1.5">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
              Prev
            </button>

            <span className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg">
              Page {page} of {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Edit User Details</h2>
                <p className="text-xs text-gray-500">Updating credentials for UID #{editUserData.uid}</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  value={editUserData.username}
                  onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  New Password (Optional)
                </label>
                <input
                  type="password"
                  placeholder="Leave blank to keep unchanged"
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  value={editUserData.password}
                  onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  New Withdrawal Password (Optional)
                </label>
                <input
                  type="password"
                  placeholder="Leave blank to keep unchanged"
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  value={editUserData.withdrawal_password}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, withdrawal_password: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleEditUser}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;